import React, { useState, useEffect } from "react";
import { NAV_MENUS } from "../layout/ActionHeader";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  InputBase,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ENV } from "../../config/env";
import { getGlobalSettings } from "../../api/globalSettings";
import "./layout.css";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";

const THEME_COLOR = "#b91c1c";
const DIGITAL_INDIA_LOGO =
  ENV.API_BASE_URL + "/uploads/digital_india_e2205058f1.png";

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [logoUrl, setLogoUrl] = useState(null);
  const [navbarTitle, setNavbarTitle] = useState("");
  const [navbarSubTitle, setNavbarSubTitle] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    fetchNavbarData();
  }, [language]);

  const departmentContactsPath =
    NAV_MENUS.find((menu) => menu.id === "contact")
      ?.items.find((item) => item.path === "/cdmp/contact-information")?.path ||
    "/cdmp/contact-information";

  const fetchNavbarData = async () => {
    try {
      const data = await getGlobalSettings(language);
      const settings = data?.[0];

      setNavbarTitle(
        settings?.navbarTitle ||
          (language === "mr"
            ? "शहर आपत्ती व्यवस्थापन आराखडा"
            : "City Disaster Management Plan")
      );

      setNavbarSubTitle(
        settings?.navbarSubTitle ||
          (language === "mr" ? "भारत सरकार" : "Government of India")
      );

      const logo = settings?.logo?.[0]?.url;
      if (logo) setLogoUrl(`${ENV.API_BASE_URL}${logo}`);
    } catch (err) {
      console.error("Navbar settings error", err);
    }
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      color="inherit"
      sx={{
        top: { xs: "48px", sm: "48px" },
        bgcolor: "#ffffff",
        borderBottom: "1px solid #e0e0e0",
        zIndex: 1200,
        height: { xs: 56, sm: 64, md: 90 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Container maxWidth="xl" sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "100%",
            position: "relative",
          }}
        >
          {/* LEFT: CONNECT DIARY */}
          {!isMobile && (
            <Box
              onClick={() => navigate(departmentContactsPath)}
              sx={{
                px: { md: 2, lg: 3 },
                py: { md: 0.5, lg: 1 },
                borderRadius: "999px",
                cursor: "pointer",
                background: "linear-gradient(135deg, #ff9800, #f57c00)",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: { md: 13, lg: 15 },
                whiteSpace: "nowrap",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 12px rgba(245, 124, 0, 0.35)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 20px rgba(245, 124, 0, 0.45)",
                },
              }}
            >
              {language === "mr" ? "कनेक्ट डायरी" : "Connect Diary"}
            </Box>
          )}

          {/* CENTER: LOGO + TITLE */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5, md: 2 },
              position: { xs: "relative", md: "absolute" },
              left: { md: "50%" },
              transform: { md: "translateX(-50%)" },
              maxWidth: { xs: "70%", sm: "60%", md: "auto" },
            }}
          >
            <Box
              onClick={() => navigate("/")}
              sx={{
                width: { xs: 40, sm: 45, md: 55, lg: 60 },
                height: { xs: 40, sm: 45, md: 55, lg: 60 },
                borderRadius: "50%",
                overflow: "hidden",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              {logoUrl && (
                <Box
                  component="img"
                  src={logoUrl}
                  alt="Logo"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: 13, sm: 15, md: 18, lg: 20 },
                  fontWeight: 700,
                  color: THEME_COLOR,
                  lineHeight: 1.2,
                }}
              >
                {navbarTitle}
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 11, sm: 12, md: 14, lg: 15 },
                  color: "#374151",
                }}
              >
                {navbarSubTitle}
              </Typography>
            </Box>
          </Box>

          {/* RIGHT: DIGITAL INDIA LOGO */}
          {!isMobile && (
            <Box
              component="img"
              src={DIGITAL_INDIA_LOGO}
              alt="Digital India"
              sx={{
                height: { md: 40, lg: 48 },
                width: "auto",
                objectFit: "contain",
              }}
            />
          )}

          {/* MOBILE: SEARCH ICON */}
          {isMobile && (
            <IconButton
              onClick={() => setShowSearch(!showSearch)}
              sx={{
                color: THEME_COLOR,
              }}
            >
              <SearchIcon />
            </IconButton>
          )}
        </Box>

        {/* MOBILE SEARCH */}
        {isMobile && showSearch && (
          <Box sx={{ mt: 1, pb: 1 }}>
            <InputBase
              fullWidth
              placeholder={language === "mr" ? "शोधा..." : "Search..."}
              startAdornment={<SearchIcon sx={{ mr: 1, color: "#666" }} />}
              sx={{
                border: "1px solid #ddd",
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                bgcolor: "#f9f9f9",
                fontSize: "0.9rem",
              }}
            />
          </Box>
        )}
      </Container>
    </AppBar>
  );
};

export default Navbar;